import React, { useContext, useMemo } from 'react';
import BonusPeriodSelector from './BonusPeriodSelector';
import BonusPeriodsContext from '../../../../contexts/BonusPeriodsContext';
import flattenBonusSubPeriods from '../../../../contextProviders/BonusAppProvider/flattenBonusSubPeriods';
import toSentenceCase from '../../../../services/toSentenceCase';
import getBonusPeriodDisplayLabel from 'formatters/getBonusPeriodDisplayLabel';

const BonusPeriodSelectorContainer = ({
  onDatePickerOpen,
  onDatePickerClose,
}: {
  onDatePickerOpen?: () => void;
  onDatePickerClose?: () => void;
}) => {
  const {
    bonusPeriods,
    selectedBonusPeriod,
    setSelectedBonusPeriod,
    selectedSubPeriod,
    setSelectedSubPeriod,
  } = useContext(BonusPeriodsContext);

  // When the programme uses sub-periods, this board selector lists the weeks
  // (like the nav BonusPeriodPicker) — boards always consume the sub-period.
  // Undefined when no period carries sub-periods (the ordinary case).
  const subPeriodOptions = useMemo(
    () => flattenBonusSubPeriods(bonusPeriods),
    [bonusPeriods],
  );

  const options = useMemo(() => {
    if (subPeriodOptions) {
      return subPeriodOptions.map(({ period, subPeriod }) => ({
        id: `${period.id}:${subPeriod.startDate}`,
        label: `${subPeriod.label} - ${toSentenceCase(period.status, true)}`,
        onSelected: () => {
          setSelectedBonusPeriod(period);
          setSelectedSubPeriod?.(subPeriod);
        },
      }));
    }
    return bonusPeriods.map((bp) => ({
      id: bp.id,
      label: `${bp.label} - ${toSentenceCase(bp.status, true)}`,
      onSelected: () => setSelectedBonusPeriod(bp),
    }));
  }, [
    subPeriodOptions,
    bonusPeriods,
    setSelectedBonusPeriod,
    setSelectedSubPeriod,
  ]);

  if (!selectedBonusPeriod) {
    return null;
  }

  const selectedLabel =
    subPeriodOptions && selectedSubPeriod
      ? getBonusPeriodDisplayLabel({
          label: selectedSubPeriod.label,
          status: selectedBonusPeriod.status,
        })
      : getBonusPeriodDisplayLabel(selectedBonusPeriod);

  return (
    <BonusPeriodSelector
      options={options}
      selectedLabel={selectedLabel}
      onDatePickerOpen={onDatePickerOpen}
      onDatePickerClose={onDatePickerClose}
    />
  );
};

export default BonusPeriodSelectorContainer;
